import React, { type ReactNode } from 'react';
import { devWarn } from '../../_internal/devWarn';
import {
  Body as BodyRoot,
  Header as HeaderRoot,
  HeaderLead as HeaderLeadRoot,
  Root,
  Title as TitleRoot,
} from './Sheet.styled';

export interface SheetProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  /** Sheet body — typically a `<Sheet.Header>` followed by `<Sheet.Body>`. */
  children: ReactNode;
  /**
   * Accessible name for the sheet. Required if {@link ariaLabelledby}
   * is not set — a `role="dialog"` region without a name is announced
   * as just "dialog" by screen readers.
   */
  ariaLabel?: string;
  /**
   * ID of the element that names this sheet (e.g. the `<Sheet.Title>`
   * id). Use instead of {@link ariaLabel} when the visible heading is
   * the canonical label and you want it kept in sync without
   * duplicating the string.
   */
  ariaLabelledby?: string;
  /**
   * `role="dialog"` defaults to `aria-modal="false"` because the
   * editor's canvas sheets coexist with the page underneath (the
   * user can still interact with the rail). Set `aria-modal="true"`
   * via the `modal` prop for sheets that capture focus.
   */
  modal?: boolean;
  dataTestId?: string;
}

export interface SheetHeaderProps
  extends React.HTMLAttributes<HTMLElement> {
  children: ReactNode;
  dataTestId?: string;
}

export interface SheetHeaderLeadProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  dataTestId?: string;
}

export interface SheetTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
  dataTestId?: string;
}

export interface SheetBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  dataTestId?: string;
}

const Header: React.FC<SheetHeaderProps> = ({
  children,
  dataTestId = 'sheet__header',
  ...rest
}) => (
  <HeaderRoot {...rest} data-testid={dataTestId}>
    {children}
  </HeaderRoot>
);

const HeaderLead: React.FC<SheetHeaderLeadProps> = ({
  children,
  dataTestId = 'sheet__header-lead',
  ...rest
}) => (
  <HeaderLeadRoot {...rest} data-testid={dataTestId}>
    {children}
  </HeaderLeadRoot>
);

const Title: React.FC<SheetTitleProps> = ({
  children,
  dataTestId = 'sheet__title',
  ...rest
}) => (
  <TitleRoot {...rest} data-testid={dataTestId}>
    {children}
  </TitleRoot>
);

const Body: React.FC<SheetBodyProps> = ({
  children,
  dataTestId = 'sheet__body',
  ...rest
}) => (
  <BodyRoot {...rest} data-testid={dataTestId}>
    {children}
  </BodyRoot>
);

/**
 * Anchored drawer / sheet — pure chrome, NO positioning. The
 * consumer is responsible for wrapping the sheet in its positioning
 * container (`position: absolute; inset: 0` for canvas sheets,
 * `position: fixed; bottom: 0` for app-shell drawers, etc).
 *
 * Compose with `<Sheet.Header>` + `<Sheet.Body>` children. The
 * header has a left lead slot (`<Sheet.HeaderLead>`) for the title
 * + adjacent context (count, avatar, status), and the rest of the
 * header right-aligns trailing actions (close button, kebab menu).
 *
 * @example
 *   <SheetWrapper>
 *     <Sheet
 *       ariaLabel="Run history for Tetra-bracket"
 *       dataTestId="run-history-sheet"
 *     >
 *       <Sheet.Header>
 *         <Sheet.HeaderLead>
 *           <Sheet.Title>Runs History</Sheet.Title>
 *         </Sheet.HeaderLead>
 *         <IconButton ariaLabel="Close" icon={<MdClose />} onClick={onClose} />
 *       </Sheet.Header>
 *       <Sheet.Body>{rows}</Sheet.Body>
 *     </Sheet>
 *   </SheetWrapper>
 */
const SheetRoot: React.FC<SheetProps> = ({
  children,
  ariaLabel,
  ariaLabelledby,
  modal = false,
  dataTestId = 'sheet',
  ...rest
}) => {
  // a11y guard: `role="dialog"` without an accessible name is announced
  // as just "dialog". Warn in dev so consumers notice the gap; we don't
  // throw because some consumers (tests, design-system docs) may render
  // a sheet purely for visual inspection.
  if (!ariaLabel && !ariaLabelledby) {
    devWarn(
      'Sheet: role="dialog" is missing an accessible name — pass `ariaLabel` or `ariaLabelledby`.',
    );
  }
  // Per ARIA, `aria-labelledby` wins when both are present. Emit only
  // one to avoid the ambiguity entirely — prefer the id reference so
  // the label stays in sync with the visible heading text.
  const a11yName = ariaLabelledby
    ? { 'aria-labelledby': ariaLabelledby }
    : { 'aria-label': ariaLabel };
  return (
    <Root
      {...rest}
      role="dialog"
      aria-modal={modal ? 'true' : 'false'}
      {...a11yName}
      data-testid={dataTestId}
    >
      {children}
    </Root>
  );
};

const Sheet = SheetRoot as typeof SheetRoot & {
  Header: typeof Header;
  HeaderLead: typeof HeaderLead;
  Title: typeof Title;
  Body: typeof Body;
};
Sheet.Header = Header;
Sheet.HeaderLead = HeaderLead;
Sheet.Title = Title;
Sheet.Body = Body;

export default Sheet;
